/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import ActionModel from 'cio/models/action';
import { capitalize } from '@ember/string';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import MetricsService from 'cio/services/metrics';
import { Entities, Periods, Measure, MEASURE_LABELS } from 'cio/utils/metrics/common';
import { MeasureType, findDenominator } from 'cio/utils/metrics/measure';
import EnvironmentService from 'cio/services/environment';
import Ember from 'ember';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { Metric } from 'cio/utils/metrics/models/metric';
import { TimeRange } from 'cio/utils/time';
import { deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import isMultiLangBranchChild from 'cio/utils/visual-workflow-builder/is-multi-lang-branch-child';
import { AVAILABLE_WORKFLOW_MEASURE_OPTIONS, NON_RATE_MEASURES } from 'cio/utils/workflow-metrics';

const LABEL_MAP: Record<string, string> = {
  ...MEASURE_LABELS,
  spammed: 'spammed',
  unsubscribed: 'unsubs',
};

interface VisualWorkflowBuilderWorkflowActionMetricsPanelArgs {
  action: ActionModel;
  period: Periods;
  timeRange: TimeRange;
  metricsTabSelectedMeasures: string[];
}

const MEASURE_SORTING = AVAILABLE_WORKFLOW_MEASURE_OPTIONS.map((m) => m.value);

export default class VisualWorkflowBuilderWorkflowActionMetricsPanel extends Component<VisualWorkflowBuilderWorkflowActionMetricsPanelArgs> {
  @service environment!: EnvironmentService;
  @service('metrics') metricsService!: MetricsService;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;

  @tracked isInViewport = false;

  @tracked metric: Metric | null = null;
  hasPreparedMetrics = false;

  constructor(
    owner: VisualWorkflowBuilderWorkflowActionMetricsPanel,
    args: VisualWorkflowBuilderWorkflowActionMetricsPanelArgs,
  ) {
    super(owner, args);

    if (Ember.testing) {
      this.isInViewport = true;
      this.prepareMetrics.perform();
    }
  }

  @action
  didUpdatePeriodOrTimeRange() {
    this.hasPreparedMetrics = false;

    if (this.isInViewport) {
      this.prepareMetrics.perform();
    }
  }

  prepareMetrics = task({ drop: true }, async () => {
    let actionId = this.args.action.id;

    let parentAction = this.args.action.campaign?.getConsolidatedActionParentBranch(actionId);
    if (parentAction != null) {
      actionId = parentAction.id;
    }

    this.metric = await this.metricsService.fetchMetric(
      Entities.ACTION,
      actionId,
      this.args.period,
      this.args.timeRange,
      this.args.action.type,
      {
        campaignId: this.args.action.belongsTo('campaign').id(),
        environmentId: this.environment.current.get('id'),
        canConvert: this.args.action.canConvert,
        version: 2,
      },
    );
    this.hasPreparedMetrics = true;
  });

  get columns() {
    let metrics = this.args.metricsTabSelectedMeasures;

    let availableActionMetrics = deliveryStatesAndActionsPerType(this.args.action.canConvert)[this.args.action.type];

    let rateMeasureStates = metrics
      .filter((metric) => !NON_RATE_MEASURES.includes(metric as Measure))
      .map((metric) => {
        let label = capitalize(LABEL_MAP[metric] || metric);
        let denominator = findDenominator(metric as Measure, MeasureType.DeliveryMetrics, this.args.action.type);

        return {
          percent: `${metric}.rate`,
          total: `${metric}.total`,
          denominatorTotal: `${denominator}.total`,
          denominatorMetric: denominator,
          label,
          metric,
          available: availableActionMetrics.includes(metric),
        };
      });

    let columns = [];

    let nonRateMeasureStates = metrics
      .filter((metric) => NON_RATE_MEASURES.includes(metric as Measure))
      .map((metric) => {
        let label = capitalize(LABEL_MAP[metric] || metric);

        return {
          total: `${metric}.total`,
          label,
          metric,
          available: availableActionMetrics.includes(metric),
        };
      });

    columns.push(...rateMeasureStates, ...nonRateMeasureStates);

    columns = columns.sort((a, b) => MEASURE_SORTING.indexOf(a.metric) - MEASURE_SORTING.indexOf(b.metric));

    return columns;
  }

  get isConsolidatedAction() {
    return isMultiLangBranchChild(this.args.action);
  }

  @action
  onEnterViewport() {
    if (Ember.testing) {
      return;
    }

    this.isInViewport = true;

    // No need to re-fetch metrics if they've already been retrieved previously.
    // Otherwise we'll keep fetching metrics every time the user scrolls to
    // reveal a metrics panel, which isn't necessary.
    if (!this.hasPreparedMetrics) {
      this.prepareMetrics.perform();
    }
  }

  @action
  onExitViewport() {
    if (Ember.testing) {
      return;
    }

    this.isInViewport = false;
  }
}
